/** Two laps on one map, and the three numbers that explain the difference.
 *
 * The chosen lap is drawn solid in the accent and the reference dashed in its
 * own colour, on the circuit itself when the community has published one: the
 * road surface as the compiler's quads, the two borders over it, and the
 * surveyed start/finish line. That is what makes a racing line readable — a
 * line on a blank page shows where you went; a line on the road shows where
 * on the road you were, which is the whole of the discipline. A circuit
 * nobody has surveyed yet has no road to draw, so the laps themselves stand
 * in, stroked thickly underneath the lines, and the card says so rather than
 * letting a road that follows your line pass for a survey.
 *
 * Everything is fitted into a single viewBox — both laps and the outline —
 * which is the only way the picture means anything: fitted separately, two
 * laps of the same circuit would be drawn at two scales and every difference
 * between them would be an artefact. But the whole picture is the wrong
 * magnification for the question: a 4 km oval fitted to a card puts its road
 * under a pixel, and the line covers it. So the map zooms — the wheel, about
 * the cursor; a drag to pan; a double-click or the fit button to come back —
 * and the strokes do not scale, so at forty times the road is a hand's width
 * and the line is still a line. The arithmetic is `geometry.ts`'s and is
 * tested there; this component only turns pointer events into it.
 *
 * Underneath, the same comparison as arithmetic. The chips are where a driver
 * actually finds the time: a line on a map shows where you were, and a sector
 * delta shows what it cost.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import { Card, ErrorNote, Kicker, Spinner } from '@/components/ui';
import { DASH, delta, deltaTone, lapTime } from '@/lib/format';
import {
  MAX_ZOOM,
  fitLines,
  hasGeometry,
  panView,
  pathFromPoints,
  pathsFromGeometry,
  roadPath,
  sectorMarks,
  startTick,
  viewBoxString,
  zoomOf,
  zoomView,
} from '@/components/sessions/geometry';
import type { Point, ViewBox } from '@/components/sessions/geometry';
import type { Reference } from '@/components/sessions/references';
import type { LapDocument, TrackGeometry } from '@/lib/types';

export interface RacingLineCardProps {
  lapNumber: number | null;
  /** The chosen lap, and the lap it is being compared against. */
  document: LapDocument | null;
  points: readonly Point[];
  referencePoints: readonly Point[];
  reference: Reference | null;
  /** The reference's own time, once its lap has been read. */
  referenceMs: number | null;
  sectorDeltas: ReadonlyArray<number | null>;
  /** The circuit, as published: `undefined` while it is being asked for,
   *  `null` when there is none, in which case the laps are the road. */
  geometry: TrackGeometry | null | undefined;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}

const TONE: Record<ReturnType<typeof deltaTone>, string> = {
  warn: 'var(--color-warn)',
  ok: 'var(--color-ok)',
  level: 'var(--color-neutral-300)',
  none: 'var(--color-neutral-600)',
};

/** One wheel notch (about a hundred units) is a fifth closer or further. */
const WHEEL_RATE = 0.002;
/** What the buttons do per press. */
const BUTTON_STEP = 1.6;

/** Screen to drawing space through the SVG's own transform, so the card's
 *  size and the letterboxing round the viewBox are both accounted for. */
function toDrawing(svg: SVGSVGElement, clientX: number, clientY: number): Point | null {
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const point = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
  return [point.x, point.y];
}

/** Drawing units per screen pixel, for turning a drag into a pan. */
function unitsPerPixel(svg: SVGSVGElement): number | null {
  const ctm = svg.getScreenCTM();
  return ctm && ctm.a !== 0 ? 1 / ctm.a : null;
}

function Legend({ reference, timeMs }: { reference: Reference | null; timeMs: number | null }) {
  if (!reference) return null;
  return (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        fontSize: 11,
        color: 'var(--color-neutral-500)',
      }}
    >
      <span
        aria-hidden="true"
        style={{ width: 14, height: 0, borderTop: `1.5px dashed ${reference.colour}` }}
      />
      {reference.label}
      <span className="tabular-nums" style={{ color: 'var(--color-neutral-300)' }}>
        {lapTime(timeMs ?? reference.timeMs)}
      </span>
    </span>
  );
}

function ZoomControls({
  zoom,
  onZoom,
  onFit,
}: {
  zoom: number;
  onZoom: (factor: number) => void;
  onFit: () => void;
}) {
  const small = { fontSize: 12, padding: '0 6px', lineHeight: '18px' } as const;
  return (
    <span
      role="group"
      aria-label="zoom"
      className="tabular-nums"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11 }}
    >
      <button
        type="button"
        className="btn btn-ghost"
        style={small}
        aria-label="zoom out"
        disabled={zoom <= 1}
        onClick={() => onZoom(1 / BUTTON_STEP)}
      >
        −
      </button>
      <span style={{ color: 'var(--color-neutral-500)', minWidth: 34, textAlign: 'center' }}>
        {zoom < 10 ? zoom.toFixed(1) : Math.round(zoom)}×
      </span>
      <button
        type="button"
        className="btn btn-ghost"
        style={small}
        aria-label="zoom in"
        disabled={zoom >= MAX_ZOOM}
        onClick={() => onZoom(BUTTON_STEP)}
      >
        +
      </button>
      {zoom > 1 ? (
        <button type="button" className="btn btn-ghost" style={small} onClick={onFit}>
          fit
        </button>
      ) : null}
    </span>
  );
}

function SectorChips({ deltas }: { deltas: ReadonlyArray<number | null> }) {
  if (deltas.length === 0) return null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${deltas.length}, minmax(0, 1fr))`,
        gap: 1,
        background: 'var(--color-divider)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        marginTop: 8,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {deltas.map((ms, index) => (
        <div key={index} style={{ background: 'var(--color-bg)', padding: '6px 10px' }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--color-neutral-500)',
            }}
          >
            {`S${index + 1} Δ`}
          </div>
          <div style={{ fontSize: 13, color: TONE[deltaTone(ms)] }}>
            {ms === null ? DASH : delta(ms)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function RacingLineCard({
  lapNumber,
  document,
  points,
  referencePoints,
  reference,
  referenceMs,
  sectorDeltas,
  geometry,
  loading,
  error,
  onRetry,
}: RacingLineCardProps) {
  const outline = hasGeometry(geometry) ? geometry : null;
  const hasLine = points.length >= 2 || referencePoints.length >= 2;
  const fit = useMemo(
    () => (hasLine ? fitLines([points, referencePoints], outline) : null),
    [hasLine, points, referencePoints, outline],
  );
  const base = fit?.view ?? null;

  // ── the view: the fit, or where the reader has taken it ───────────────────

  const [view, setView] = useState<ViewBox | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const drag = useRef<{ id: number; x: number; y: number } | null>(null);

  // Another lap or another session is another picture, and starts whole.
  const baseKey = base ? viewBoxString(base) : '';
  useEffect(() => {
    setView(null);
  }, [baseKey]);

  const shown = view ?? base;
  const zoom = base && shown ? zoomOf(base, shown) : 1;

  const zoomBy = useCallback(
    (factor: number, about?: Point) => {
      if (!base) return;
      setView((current) => {
        const from = current ?? base;
        const anchor: Point = about ?? [from.x + from.width / 2, from.y + from.height / 2];
        const next = zoomView(base, from, factor, anchor);
        return zoomOf(base, next) <= 1.001 ? null : next;
      });
    },
    [base],
  );

  // The wheel has to be a native listener: React registers `wheel` as
  // passive, and a passive listener cannot stop the page scrolling under
  // the map.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !base) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const about = toDrawing(svg, event.clientX, event.clientY) ?? undefined;
      zoomBy(Math.exp(-event.deltaY * WHEEL_RATE), about);
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [base, zoomBy]);

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.button !== 0 || zoom <= 1) return; // nothing to pan at the fit
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
  };

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    const held = drag.current;
    if (!held || held.id !== event.pointerId || !base) return;
    const per = unitsPerPixel(event.currentTarget);
    if (per === null) return;
    const dx = (held.x - event.clientX) * per;
    const dy = (held.y - event.clientY) * per;
    drag.current = { ...held, x: event.clientX, y: event.clientY };
    setView((current) => panView(base, current ?? base, dx, dy));
  };

  const endDrag = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (drag.current?.id !== event.pointerId) return;
    drag.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Already released — the pointer left, or the capture never took.
    }
  };

  // ── what is drawn ─────────────────────────────────────────────────────────

  const marks = sectorMarks(document, points);
  // Sized to the view on screen, not the fit: text in a fitted viewBox is in
  // the box's own units, and a label the size of the whole picture is not a
  // label at forty times.
  const labelSize = shown ? shown.width * 0.028 : 0;
  // The lap's own crossing marks the start only when there is no surveyed
  // line to show; two marks at one place would be one too many.
  const tick = shown && !outline ? startTick(points, shown.width * 0.035) : null;
  const paths = outline ? pathsFromGeometry(outline) : null;
  const road = outline ? roadPath(outline) : '';

  return (
    <Card elevation="none" style={{ padding: 12, gap: 6 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Kicker>racing line · lap {lapNumber ?? DASH}</Kicker>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {geometry === null && shown ? (
            <span style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>
              no outline published · the road is the laps&rsquo; own path
            </span>
          ) : null}
          <Legend reference={reference} timeMs={referenceMs} />
          {shown ? (
            <ZoomControls zoom={zoom} onZoom={(factor) => zoomBy(factor)} onFit={() => setView(null)} />
          ) : null}
        </span>
      </div>

      {error ? (
        <ErrorNote
          compact
          error={error}
          title="could not draw this lap"
          onRetry={onRetry}
        />
      ) : !shown ? (
        <div
          style={{
            display: 'grid',
            placeItems: 'center',
            minHeight: 180,
            fontSize: 12.5,
            color: 'var(--color-neutral-600)',
            textAlign: 'center',
          }}
        >
          {loading ? (
            <Spinner label="loading the lap" />
          ) : (
            'This lap has no racing line stored — the logger sent its times but not its position.'
          )}
        </div>
      ) : (
        <>
          <svg
            ref={svgRef}
            viewBox={viewBoxString(shown)}
            role="img"
            aria-label={`Racing line for lap ${lapNumber ?? ''}${
              reference ? `, against ${reference.label}` : ''
            }${outline ? ', on the published circuit' : ''}${
              zoom > 1 ? `, zoomed ${zoom.toFixed(1)} times` : ''
            }`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onDoubleClick={() => setView(null)}
            style={{
              width: '100%',
              maxHeight: 280,
              display: 'block',
              // Whole, the labels near the edge may spill a little and should;
              // zoomed, what is outside the view must not appear.
              overflow: zoom > 1 ? 'hidden' : 'visible',
              touchAction: 'none',
              cursor: zoom > 1 ? 'grab' : 'zoom-in',
              userSelect: 'none',
            }}
          >
            {outline && paths ? (
              <>
                {/* The road as the compiler measured it: quads between the
                    two borders, at their real width. A hair of stroke in the
                    same colour hides the anti-aliased seam between neighbours. */}
                {road ? (
                  <path
                    d={road}
                    fill="var(--color-neutral-700)"
                    stroke="var(--color-neutral-700)"
                    strokeWidth={0.8}
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ) : null}
                {[...paths.left, ...paths.right].map((d, index) => (
                  <path
                    key={index}
                    d={d}
                    fill="none"
                    stroke="var(--color-neutral-500)"
                    strokeWidth={1}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
                {paths.finish ? (
                  <line
                    x1={paths.finish[0][0]}
                    y1={paths.finish[0][1]}
                    x2={paths.finish[1][0]}
                    y2={paths.finish[1][1]}
                    stroke="var(--color-neutral-300)"
                    strokeWidth={1.6}
                    vectorEffect="non-scaling-stroke"
                  />
                ) : null}
              </>
            ) : (
              // No survey: the bed is both laps, stroked thickly, so the road
              // is the ground they actually covered rather than a guess at its
              // edges.
              [referencePoints, points].map((run, index) => {
                const d = pathFromPoints(run);
                return d ? (
                  <path
                    key={index}
                    d={d}
                    fill="none"
                    stroke="var(--color-neutral-800)"
                    strokeWidth={20}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ) : null;
              })
            )}

            {referencePoints.length >= 2 && reference ? (
              <path
                d={pathFromPoints(referencePoints)}
                fill="none"
                stroke={reference.colour}
                strokeWidth={1.4}
                strokeDasharray="4 3"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}

            {points.length >= 2 ? (
              <path
                d={pathFromPoints(points)}
                fill="none"
                stroke="var(--color-accent)"
                strokeWidth={1.8}
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            ) : null}

            {tick ? (
              <line
                x1={tick[0][0]}
                y1={tick[0][1]}
                x2={tick[1][0]}
                y2={tick[1][1]}
                stroke="var(--color-neutral-300)"
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
              />
            ) : null}

            {marks.map((mark) => (
              <text
                key={mark.label}
                x={mark.point[0]}
                y={mark.point[1] - labelSize * 0.8}
                fill="var(--color-neutral-600)"
                fontSize={labelSize}
                fontFamily="var(--font-body)"
                textAnchor="middle"
              >
                {mark.label}
              </text>
            ))}
          </svg>
          <div style={{ fontSize: 10.5, color: 'var(--color-neutral-600)', textAlign: 'right' }}>
            scroll to zoom · drag to pan · double-click to fit
          </div>
        </>
      )}

      <SectorChips deltas={sectorDeltas} />
    </Card>
  );
}

export default RacingLineCard;
